import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { Badge } from 'react-bootstrap';

// ============= CONSTANTS =============
export const SCORE_PASS_THRESHOLD = 70;
export const DAYS_FOR_ACTIVE_WINDOW = 7;
export const DEFAULT_PAGE_SIZE = 10;
export const MAX_TABLE_HEIGHT = '50vh';
export const EXPANDED_COLUMN_WIDTH = 150;

export const ResourceTypeLabelsList = [
    { name: 'Assessment (Test / Quiz)', value: 0 },
    { name: 'Question', value: 1 },
    { name: 'Text', value: 2 },
    { name: 'Video', value: 3 },
    { name: 'Documents & Files', value: 4 },
    { name: 'Links & External Content', value: 5 }
];

/** Localized version of ResourceTypeLabelsList for display (dropdowns, chips). */
export const useResourceTypeLabelsList = () => {

    const { formatMessage } = useIntl();

    return [
        { name: formatMessage({ id: 'assessment_test_quiz.9c43a59a', defaultMessage: 'Assessment (Test / Quiz)' }), value: 0 },
        { name: formatMessage({ id: 'question.e7d7defa', defaultMessage: 'Question' }), value: 1 },
        { name: formatMessage({ id: 'text.ba3826d2', defaultMessage: 'Text' }), value: 2 },
        { name: formatMessage({ id: 'video.bb8e71c3', defaultMessage: 'Video' }), value: 3 },
        { name: formatMessage({ id: 'documents_and_files.854cceb3', defaultMessage: 'Documents & Files' }), value: 4 },
        { name: formatMessage({ id: 'links_and_external_content.73f34309', defaultMessage: 'Links & External Content' }), value: 5 }
    ];

};

// ============= UTILITY FUNCTIONS =============

/**
 * Converts minutes to a formatted string (e.g., "2h 30m" or "45 min")
 */
export const calculateHourMinFormat = (minutes: number): string => {

    if (minutes >= 60) {

        return `${Math.floor(minutes / 60)}h ${Math.floor(minutes % 60)}m`;

    } else {

        return `${Math.floor(minutes) ?? 0} min`;

    }

};

/**
 * Determines the badge variant based on score threshold
 */
export const getBadgeVariant = (score: number | null): string => {

    if (!score) return 'secondary';

    // Normalize score to 0-100 for threshold comparison
    const normalizedScore = score > 100 ? score / 10 : score;
    return normalizedScore >= SCORE_PASS_THRESHOLD ? 'success' : 'danger';

};

/**
 * Normalizes score to 0-100 scale (converts 1000-scale if needed)
 */
export const normalizeScore = (score: number | null): number => {

    if (!score) return 0;

    // Convert 1000-scale to 100-scale if needed
    return score > 100 ? Math.floor(score / 10) : score;

};

/**
 * Renders a score badge with appropriate styling
 */
export const renderScore = (score: number | null, isAverage = false) => (
    <Badge
        pill
        bg={getBadgeVariant(score)}
        style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
    >
        {isAverage ? (
            <>
                <FormattedMessage defaultMessage="Avg. Score" id="avg_score.79522af6" />
                {' '}
            </>
        ) : ''}
        {Math.floor(normalizeScore(score))}
        {' '}
        %
    </Badge>
);

/**
 * Determines status based on item data
 * - Assessment (ResourceTypeID 0): Complete if at least 1 test submitted (Attempts >= 1)
 * - Video (ResourceTypeID 1): Trust the SP's Completed flag so partial progress
 *   (e.g. "8/15" clips viewed) isn't misreported as Complete.
 * - All others: Complete upon opening the resource (CourseStudentResourceId exists)
 */
export const getStatus = (item: any): string => {

    const isAssessment = item?.ResourceTypeID === 0;
    const isVideo = item?.ResourceTypeID === 1;
    const hasCourseStudentResource = item?.CourseStudentResourceId !== null && item?.CourseStudentResourceId !== undefined;

    if (isAssessment) {

        if ((item?.Attempts ?? 0) >= 1) return 'Complete';
        if (hasCourseStudentResource) return 'In Progress';
        return 'Not Started';

    }

    if (isVideo) {

        const videoComplete = item?.Completed?.Share === '1'
            || item?.Completed?.Answered === true
            || (item?.ResourceDateCompleted !== null && item?.ResourceDateCompleted !== undefined);
        if (videoComplete) return 'Complete';
        if (hasCourseStudentResource) return 'In Progress';
        return 'Not Started';

    }

    // All other resources: complete upon opening
    if (hasCourseStudentResource) return 'Complete';
    return 'Not Started';

};

/**
 * Renders status with icon and styling
 */
export const renderStatus = (status: string) => {

    // Keys are the canonical identifiers returned by getStatus; labels are localized at render.
    const statusConfig = {
        'Complete': { bgColor: '#f0fdfa', color: '#0f766e', icon: 'fa-circle-check', label: <FormattedMessage defaultMessage="Complete" id="complete.6757643c" /> },
        'Completed': { bgColor: '#f0fdfa', color: '#0f766e', icon: 'fa-circle-check', label: <FormattedMessage defaultMessage="Completed" id="completed.cd3d7862" /> },
        'In Progress': { bgColor: '#f0f9ff', color: '#0284c7', icon: 'fa-circle', label: <FormattedMessage defaultMessage="In Progress" id="in_progress.0e4b5895" /> },
        'Not Started': { bgColor: '#fafafa', color: '#71717a', icon: 'fa-circle', label: <FormattedMessage defaultMessage="Not Started" id="not_started.6c6f679c" /> }
    };

    const config = statusConfig[status as keyof typeof statusConfig];

    return (
        <div className="d-flex align-items-center justify-content-center gap-2">
            <i
                className={`fa-solid ${config.icon}`}
                style={{ color: config.color, fontSize: '1.1rem' }}
            />
            <span style={{ fontWeight: 500 }}>
                {config.label}
            </span>
        </div>
    );

};

/**
 * Formats a date value to US locale format
 */
export const formatDate = (val: any): string => {

    if (!val) return '---';
    try {

        const date = typeof val === 'string' ? new Date(val) : val;
        return new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);

    } catch {

        return String(val);

    }

};

/**
 * Checks if a date is within the last N days
 */
export const isDateWithinLast7Days = (inputDate: Date): boolean => {

    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - DAYS_FOR_ACTIVE_WINDOW);
    const inputTimestamp = inputDate.getTime();
    return inputTimestamp >= sevenDaysAgo.getTime() && inputTimestamp <= now.getTime();

};

/**
 * Extracts unique student names from data
 */
export const getUniqueStudentNames = (data: any) => {

    const studentMap = new Map();
    for (const item of data) {

        if (item.StudentProfileId && !studentMap.has(item.StudentProfileId)) {

            studentMap.set(item.StudentProfileId, {
                StudentName: item.StudentName,
                StudentProfileId: item.StudentProfileId
            });

        }

    }
    return Array.from(studentMap.values());

};

/**
 * Renders sortable header content with sort-direction indicators.
 * The parent <th> should handle onClick for triggering sort changes.
 */
export const sortableHeader = (content: React.ReactNode, field?: string, sort?: any[]) => {

    const dir = sort?.find((s: any) => s.field === field)?.dir;
    return (
        <span className="k-link d-flex align-items-center justify-content-center gap-1">
            {content}
            {dir === 'asc' && <i aria-hidden="true" className="fa-solid fa-arrow-up-long" />}
            {dir === 'desc' && <i aria-hidden="true" className="fa-solid fa-arrow-down-long" />}
        </span>
    );

};

/**
 * Compares two values for sorting (handles nulls, numbers, and strings)
 */
export const compareValues = (a: any, b: any): number => {

    if (a === null || a === undefined) return b === null || b === undefined ? 0 : -1;
    if (b === null || b === undefined) return 1;
    const numA = Number(a);
    const numB = Number(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return String(a).localeCompare(String(b));

};

/**
 * Calculates average from an array of values
 */
export const avg = (arr: any[]): number => {

    const nums = arr.filter(v => v !== null && v !== undefined).map(Number);
    return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;

};